<script lang="ts" setup>
import type { PropType } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import { accountService } from '@exchange/libs/account/service/src';

const props = defineProps({
  closeMenu: { type: Function as PropType<() => void>, default: () => {} },
  showDepositWithdraw: { type: Boolean, default: true },
  showDepositOnly: { type: Boolean, default: false },
  short: { type: Boolean, default: false },
});

const router = useRouter();
const route = useRoute();
const { userIsBroker } = accountService;

const navigateTo = ({ hash }: { hash: string }) => {
  router.push({
    ...route,
    hash: `#${hash}`,
  });
};

const fundingActionsTriggered = (action: 'Deposit' | 'Withdraw' | 'Transfer') => {
  props.closeMenu();
  navigateTo({ hash: action });
};
</script>

<template>
  <div
    v-if="!short"
    :class="['funding-actions-triggers funding-actions-triggers--large', { 'funding-actions-triggers--deposit-only': showDepositOnly }]"
  >
    <template v-if="showDepositWithdraw || showDepositOnly">
      <x-button
        id="deposit-trigger"
        :block="true"
        size="xs"
        testid="funding-deposit"
        variant="success"
        @click="fundingActionsTriggered('Deposit')"
      >
        <ot-icon
          class="funding-actions-triggers__icon"
          name="received"
        />
        <span class="funding-actions-triggers__title">{{ $t('fundamentals.addFunds') }}</span>
      </x-button>
      <x-button
        v-if="showDepositWithdraw"
        id="withdraw-trigger"
        :block="true"
        size="xs"
        testid="funding-withdraw"
        variant="tertiary"
        @click="fundingActionsTriggered('Withdraw')"
      >
        <ot-icon
          class="funding-actions-triggers__icon"
          name="send"
        />
        <span class="funding-actions-triggers__title">{{ $t('fundamentals.withdraw') }}</span>
      </x-button>
    </template>
    <x-button
      v-if="!showDepositOnly && !userIsBroker"
      id="transfer-trigger"
      :block="true"
      size="xs"
      testid="funding-transfer"
      variant="tertiary"
      @click="fundingActionsTriggered('Transfer')"
    >
      <ot-icon
        class="funding-actions-triggers__icon"
        name="arrow-swap-horizontal"
      />
      <span class="funding-actions-triggers__title">{{ $t('fundamentals.transfer') }}</span>
    </x-button>
  </div>
  <div
    v-else
    class="funding-actions-triggers funding-actions-triggers--short"
    :class="{ 'funding-actions-triggers--broker': userIsBroker }"
  >
    <x-button
      id="deposit-trigger"
      testid="funding-deposit"
      variant="success"
      size="sm"
      :text="$t('fundamentals.addFunds')"
      @click="fundingActionsTriggered('Deposit')"
    />
    <x-button
      id="withdraw-trigger"
      class="funding-actions-triggers__withdraw-transfer-button"
      variant="tertiary"
      size="3xs"
      @click="fundingActionsTriggered('Withdraw')"
    >
      <ot-icon
        class="funding-actions-triggers__withdraw-transfer-icon"
        name="send"
      />
    </x-button>
    <x-button
      v-if="!userIsBroker"
      id="transfer-trigger"
      class="funding-actions-triggers__withdraw-transfer-button"
      variant="tertiary"
      size="3xs"
      @click="fundingActionsTriggered('Transfer')"
    >
      <ot-icon
        class="funding-actions-triggers__withdraw-transfer-icon"
        name="arrow-swap-horizontal"
      />
    </x-button>
  </div>
</template>

<style lang="scss">
.funding-actions-triggers {
  display: grid;
  grid-gap: 8px;

  &__icon {
    width: 16px;
    margin-right: 6px;

    .xs & {
      margin-right: 4px;
    }
  }
}

.funding-actions-triggers--deposit-only {
  grid-template-columns: 1fr;
}

.funding-actions-triggers--large.funding-actions-triggers {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: auto;

  #withdraw-trigger,
  #transfer-trigger {
    margin: 0;
  }

  #transfer-trigger {
    grid-column: 1 / -1;
  }
}

.funding-actions-triggers--short {
  &.funding-actions-triggers {
    color: rgb(var(--v-theme-text-secondary));
    grid-template-columns: auto repeat(2, 1fr);
  }

  &.funding-actions-triggers--broker {
    grid-template-columns: auto 1fr;
  }
}
</style>
